import moment from 'moment-timezone';
import * as types from './types';

/**
 * Return JS DateTime from string in actual timezone.
 *
 * @param {string} time - Time string (should contain timezone).
 */
export function convertUtcToUserTimezone(time) {
  const timezone = window.sessionStorage.getItem('timezone');

  const dateTime = new Date(time).toLocaleString(
     'en-US', {timeZone: timezone}
  );

  // Return `-` if time string is not DateTime
  if (dateTime === 'Invalid Date') return '-';

  return dateTime;
}

/**
 * Return string in Title Case.
 *
 * @param {string} str - String to make in title case.
 */
export function toTitleCase(str) {
	str = str.toLowerCase().split(' ');

	for (let i = 0; i < str.length; i++) {
		str[i] = str[i].charAt(0).toUpperCase() + str[i].slice(1);
	}

	return str.join(' ');
}


/**
 * Sleep for some time.
 *
 * @param {int} ms - Sleeping time in ms.
 */
export function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Run callback with delay
 * @param {Function} fn Function to call.
 * @param {number} ms Delay in milliseconds.
 */
export function delay(fn, ms = 300) {
  let timer = 0;

  return function(...args) {
    clearTimeout(timer);
    timer = setTimeout(fn.bind(this, ...args), ms || 0);
  };
}

/**
 * Print content on safari browser
 * @param {Function} hideCallback It's called when need to hide content that should not be printed.
 * @param {Function} showCallback It's called when printing is finished and we have to display default content.
 *
 * Safari has an issue with printing more than once. Popup appears and javascript thread is not blocked.
 * So when a user accept printing, our function had already finished and we print full page instead of
 * content we want to print.
 *
 * @see {@link https://stackoverflow.com/questions/48524702/safari-not-allowing-a-second-window-print}
 */
export function safariPrint(showCallback) {
  // Safari 12, macOS
  const mediaQueryList = window.matchMedia('print');

  const onAfterPrint = mql => {
    if (!mql.matches) {
      showCallback();

      // printing is finished => unsubscribe to avoid leaks
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', onAfterPrint);
      } else {
        mediaQueryList.removeListener(onAfterPrint);
      }
    }
    window.onfocus = null;
  };

  // a tiny polyfill for a plain onafterprint
  // to handle standard window.print() dialog events

  if (mediaQueryList.addEventListener) {
    mediaQueryList.addEventListener('change', onAfterPrint);
  } else {
    mediaQueryList.addListener(onAfterPrint);
  }

  // if a user cancels printing in Safari's print confirmation dialog
  // then we will trigger a cleanup
  window.focus();
  window.onfocus = () => {
    onAfterPrint(mediaQueryList);
  };

  window.print();
}


/**
 * Prepare a sort string for API.
 * @param {string} field
 * @param {'asc' | 'desc'} direction
 */
export function getOrderingQuery(field, direction) {
  return direction === 'asc' ? field : `-${field}`;
}

/**
 * Copy passed text to clipboard.
 * @param {string} text Text to copy.
 *
 * @returns {boolean} If text was copied.
 */
export function copyToClipboard(text) {
  try {
    // Clipboard object is available only with secure connection.
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    } else {
      // Add fallback just in case.
      const hiddenTextarea = document.createElement('textarea');

      hiddenTextarea.value = text;

      // Avoid scrolling to bottom
      hiddenTextarea.style.top = '0';
      hiddenTextarea.style.left = '0';
      hiddenTextarea.style.position = 'fixed';

      document.body.appendChild(hiddenTextarea);
      hiddenTextarea.focus();
      hiddenTextarea.select();
      document.execCommand('copy');
      document.body.removeChild(hiddenTextarea);
    }

    return true;
  } catch (err) {
    return false;
  }
}

/**
 * Get string with driver's working days.
 * @param {types.Driver} driver Driver data
 */
export function getDriversWorkingDays(driver) {
  const working = [
    {
      index: 0,
      is_working: driver.is_working_on_monday,
      weekday: 'Monday',
    },
    {
      index: 1,
      is_working: driver.is_working_on_tuesday,
      weekday: 'Tuesday',
    },
    {
      index: 2,
      is_working: driver.is_working_on_wednesday,
      weekday: 'Wednesday',
    },
    {
      index: 3,
      is_working: driver.is_working_on_thursday,
      weekday: 'Thursday',
    },
    {
      index: 4,
      is_working: driver.is_working_on_friday,
      weekday: 'Friday',
    },
    {
      index: 5,
      is_working: driver.is_working_on_saturday,
      weekday: 'Saturday',
    },
    {
      index: 6,
      is_working: driver.is_working_on_sunday,
      weekday: 'Sunday',
    },
  ];

  const workingDays = working
    .filter(day => day.is_working)
    .map(day => day.weekday)
    .join(', ');

  return workingDays;
}

/**
 * Converts time string to number for sorting.
 * @param {string} time Time string, example: '06:00'.
 */
export function getNumberFromTimeString(time) {
  return Number(time.replace(/:/g, ''));
}

/**
 * Open select.
 * @param {string} element Select to open.
 * @param {number} size Select options length.
 */
export function openSelectDropdown(element, size) {
  element.attr('size', size);
}

/**
 * Close select.
 * @param {string} element Select to close.
 */
export function closeSelectDropdown(element) {
  element.attr('size', 1);
}

/**
 * Download a file.
 * @param {Uint8Array} file File to download.
 * @param {name} name Name of the file.
 */
export function fileDownload(file, name) {
  const url = window.URL.createObjectURL(new Blob([file]));
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  link.remove();
};


/**
 * Format boolean value to Yes/No.
 * @param {boolean} value
 */
export function booleanFormatter(value) {
  if (value == null) {
    return '';
  }

  return value ? 'Yes' : 'No';
}


/**
 * Check if filter should have a Date value.
 * @param {string} field Field name to check.
 */
export function isDateField(field) {
  const dateFields = [
    'dispdate',
    'statdate',
    'etadate',
    'revised_eta',
    'qry_ran_on',
  ];

  return dateFields.includes(field);
}

/**
 * Convert DateTime to user timezone and common format.
 *
 * @param {string} datetime - String DateTime.
 * @param {string} timezone - TimeZone of `datetime`.
 */
export function convertTimezoneFormat(datetime, timezone) {
  let resultDateTime = null;

  if (timezone === '') return moment.utc(datetime).format('L LTS');

  // Check datetime string for origin format
  if (
    moment(datetime, 'YYYY-MM-DDTHH:mm:ssZ', true).isValid() ||
    moment(datetime, 'YYYY-MM-DD HH:mm:ss', true).isValid()
  ) {
    resultDateTime = convertUtcToUserTimezone(datetime);
  } else if (moment(datetime, 'YYYY-MM-DD', true).isValid()) {
    resultDateTime = moment(datetime).format('l');
  }

  return resultDateTime;
}

/**
 * Render complex JSON data.
 *
 * @param {object} data - JSON data.
 */
export function renderJSON(data) {
  const result = document.createElement('ul');

  // TODO (Dmitri): configure eslint properly.
  // eslint-disable-next-line guard-for-in
  for (const key in data) {
    const propertyKeys = Object.keys(data[key] || []);

    if (!data[key] || propertyKeys.length === 0) {
      // If data for key is empty, then display just `-`.
      data[key] = '-';
    }

    if (propertyKeys.length === 1 && propertyKeys[0] === 'id') {
      // In order not to show `id` title for a concise look
      data[key] = data[key].id;
    }

    // Convert value to necessary format if it DateTime string
    data[key] =
      convertTimezoneFormat(data[key], data[`${key}_timezone`]) || data[key];

    const list = document.createElement('li');

    // In API, it get keys in the form like `storage_first_chargeable_day`,
    // so it is necessary to bring them in a human-readable form
    const title =
      convertTimezoneFormat(key) || toTitleCase(key.replaceAll('_', ' '));

    const textNode = document.createElement('span');

    textNode.innerHTML = `<b>${title}: </b>`;
    list.appendChild(textNode);

    if (data[key] instanceof Object) {
      // Iterate over the nested objects recursively to display
      // the complete table
      list.appendChild(renderJSON(data[key]));
    } else {
      textNode.innerHTML += `${data[key]}`;
    }

    result.appendChild(list);
  }

  return result;
}

/**
 * Create new table cell for report.
 * @param {string} text Cell text
 * @returns HTMLTableCellElement
 */
export const createTableCell = (text) => {
  const el = document.createElement('td');

  el.innerText = text;
  el.style.padding = '2px 5px';

  return el;
};
